import Decimal from 'decimal.js';

import { BalanceSheetRowType } from '@/types/schema';

export interface RowValue {
  value: Decimal | null;
  description?: string;
  /**
   * Raw secondary value backing `description` (e.g. insurance cash value), kept
   * so recalculated totals can rebuild the description from visible columns.
   */
  secondaryValue?: Decimal | null;
  secondaryValueDescription?: string | null;
  /**
   * Whether the underlying insurance policy kind can carry a cash value. `false`
   * (e.g. term life) renders "--" for the secondary line instead of an amount;
   * `null` means not applicable. Carried through so recalculated totals stay
   * coherent.
   */
  supportsCashValue?: boolean | null;
}

/**
 * FE-only row variant for the "Subtotals" row, derived from the backend's
 * NetWorth row. The backend doesn't distinguish between subtotals and grand
 * total, so we split them on the client.
 */
export type RowDisplayVariant = BalanceSheetRowType | 'SUBTOTALS';

export interface RowData {
  id: string;
  name: {
    lineOne: string;
    lineTwo: string | undefined;
  };
  // this is used for "tree data", which is the MUI data grid row collapsing feature
  path: string[];
  // map of columnId to value
  values: Record<string, RowValue>;
  variant: BalanceSheetRowType;
  /**
   * Controls visual styling. Defaults to `variant` but can be overridden
   * for FE-derived rows (e.g., 'SUBTOTALS').
   */
  displayVariant?: RowDisplayVariant;
  // we use this to determine if a row can be expanded/collapsed
  hasChildren: boolean;

  // a row will either have entityIds or liabilityId, but not both
  entityIds?: string[];
  liabilityId?: string;
}

export const BALANCE_SHEET_TABLE_CLASS_NAME = 'BalanceSheetTable';
